import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Control {
  keys: string;
  title: string;
  body: string;
}

const CONTROLS: Control[] = [
  {
    keys: '← →',
    title: 'Turn',
    body: 'Points the motor. It does not push you sideways — it changes which way the thrust will go when you use it, which is a slower thing and the reason turning early matters.',
  },
  {
    keys: '↑',
    title: 'Burn',
    body: 'Full thrust, out of the bottom of the craft, in whatever direction the craft is currently facing. There is no throttle: it is on or it is off, and it is drinking the tank either way. Space does the same job.',
  },
  {
    keys: 'M',
    title: 'Sound',
    body: 'Off and on. The motor is the only sound you actually need — it is how you know you are still burning when you have stopped looking at the fuel.',
  },
];

interface Limit {
  figure: string;
  title: string;
  body: string;
}

const LIMITS: Limit[] = [
  {
    figure: '34',
    title: 'Descent',
    body: 'How fast you may still be falling when the legs touch. The most forgiving of the three, and the one everyone watches.',
  },
  {
    figure: '20',
    title: 'Drift',
    body: 'Sideways speed, and the one that actually gets people. A drift too small to see at full view is a drift that snaps a leg off.',
  },
  {
    figure: '±9°',
    title: 'Tilt',
    body: 'How far off upright the craft may be. It reads LEVEL or OFF rather than a number, because there is nothing useful to do with the number.',
  },
];

export function DescentHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Put the lander down on a marked pad, upright and slow. There are no brakes and no second
          tank — only a motor pointing one way and a rock pulling the other.
        </p>
      </header>

      <section className="prose__section">
        <h2>The controls</h2>
        <ul className="rulelist">
          {CONTROLS.map((control) => (
            <li key={control.keys} className="rulelist__item">
              <span className="rulelist__rank" aria-hidden="true">
                {control.keys}
              </span>
              <div>
                <h3>{control.title}</h3>
                <p>{control.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="prose__note">
          On a phone the two turn buttons are bottom left and the burn is bottom right, which is the
          way round the cabinet had them.
        </p>
      </section>

      <section className="prose__section">
        <h2>What counts as landing</h2>
        <p>
          All three of these at once, on a pad, or it is a crash. The readouts down the right of the
          screen go green when a figure is inside its limit and red when it is not, so the question
          is answerable at a glance without knowing the numbers.
        </p>
        <ul className="rulelist">
          {LIMITS.map((limit) => (
            <li key={limit.figure} className="rulelist__item">
              <span className="rulelist__rank" aria-hidden="true">
                {limit.figure}
              </span>
              <div>
                <h3>{limit.title}</h3>
                <p>{limit.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <p>
          Miss the pad and it does not matter how gently you arrived. The wreck tells you which of
          the four it was, because &ldquo;you crashed&rdquo; is not something anyone can learn from.
        </p>
      </section>

      <section className="prose__section">
        <h2>Three pads, and a decision</h2>
        <p>
          Every level has a wide pad worth <strong>×2</strong>, and narrower ones worth{' '}
          <strong>×3</strong> and <strong>×5</strong>. The multiplier applies to everything the
          landing pays: the touchdown itself, the bonus for coming down softly, and the fuel you are
          given back. A greedy landing is worth roughly two and a half of a safe one.
        </p>
        <p>
          Which is the whole game, because the pads get narrower as the levels go on and the gravity
          goes up with them. There is always something you could safely have landed on. The question
          is whether you had the fuel to reach the other one.
        </p>
      </section>

      <section className="prose__section">
        <h2>Fuel is the real clock</h2>
        <p>
          One tank across the whole run, not one per landing. Every burn spends it, including the
          burns you did not need — a correction made early costs a fraction of what the same
          correction costs at two hundred feet, and that difference is most of the difference between
          a long run and a short one.
        </p>
        <p>
          Landing well pays some back. Crashing costs you a lander, of which you have three, and
          tops the tank up enough to be worth trying again with. Run the tank dry in mid-air and
          nothing dramatic happens: the motor simply stops answering, and you find out what your
          last burn bought you.
        </p>
      </section>

      <section className="prose__section">
        <h2>The zoom</h2>
        <p>
          The view closes in as you come down, and it is not decoration. At full view the last few
          feet of a descent are a handful of pixels and every landing looks the same; close in, the
          gap between a leg and the rock is something you can actually fly. It is the original
          cabinet&rsquo;s trick and it is the reason the game is playable at all.
        </p>
        <p>
          The ground wraps. Fly off one side and you come back on the other, over the same terrain —
          so a lander that has drifted somewhere hopeless can keep going rather than be pinned
          against the edge of the world watching the tank empty.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
